package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.layout.engine.LayoutMode
import top.wcpe.allincore.ui.runtime.internal.collections.readOnlyCopy
import kotlin.jvm.JvmStatic

/**
 * 瀑布流虚拟容器：列宽固定、每项高度可变，按「当前最短列优先」算法填充，仅实例化视窗内的项。
 *
 * 项高度由 [itemHeights] 提供（预先生成的 List<Float>，MVP 不支持动态变化）。列宽 [itemWidth] 为 0 时
 * 按视窗宽度在 [columns] 列间平分。布局结果（每项的 col/x/y/w/h）由 [recomputeLayouts] 一次算出并缓存，
 * 滚动只改变窗口范围不重算布局。
 *
 * 窗口子节点 absolute 定位，坐标由 IrInterpreter 按 [itemX] / [itemY] / 列宽 / [itemHeight] 注入。
 */
@Suppress("TooManyFunctions", "MagicNumber")
class VirtualMasonry private constructor() : ScrollableContainer<VirtualMasonry>() {

    private var columnsField = 2
    private var gapXField = 4f
    private var gapYField = 4f
    private var itemWidthField = 0f
    private var bufferField = 100f
    private var itemHeightsField: List<Float?> = emptyList()

    /** 缓存的每项布局（col/x/y/w/h），随 itemHeights/columns/viewport 变化重算。 */
    private var layoutX = FloatArray(0)
    private var layoutY = FloatArray(0)
    private var layoutH = FloatArray(0)
    private var colWidth = 0f
    private var totalHeight = 0f

    private var windowChildren: List<UIElement<*>> = emptyList()
    private var lastFirst = -1
    private var lastLast = -1
    private var lastCount = -1
    private var materializer: WindowMaterializer? = null

    init {
        styleField.layout().mode = LayoutMode.ABSOLUTE
    }

    override fun self(): VirtualMasonry = this

    // -------- 属性 --------

    fun columns(c: Int): VirtualMasonry = apply {
        columnsField = c.coerceAtLeast(1)
        recomputeLayouts()
        invalidateWindow()
    }

    fun columns(): Int = columnsField

    fun gapX(g: Float): VirtualMasonry = apply {
        gapXField = g.coerceAtLeast(0f)
        recomputeLayouts()
        invalidateWindow()
    }

    fun gapX(): Float = gapXField

    fun gapY(g: Float): VirtualMasonry = apply {
        gapYField = g.coerceAtLeast(0f)
        recomputeLayouts()
        invalidateWindow()
    }

    fun gapY(): Float = gapYField

    fun itemWidth(w: Float): VirtualMasonry = apply {
        itemWidthField = w.coerceAtLeast(0f)
        recomputeLayouts()
        invalidateWindow()
    }

    fun itemWidth(): Float = itemWidthField

    fun buffer(b: Float): VirtualMasonry = apply {
        bufferField = b.coerceAtLeast(0f)
        invalidateWindow()
    }

    fun buffer(): Float = bufferField

    /** 注入每项高度列表（预生成）。null 视为空列表。 */
    fun itemHeights(heights: List<Float?>?): VirtualMasonry = apply {
        itemHeightsField = heights ?: emptyList()
        recomputeLayouts()
        scrollY(scrollY())
        invalidateWindow()
    }

    // 只读拷贝（readOnlyCopy 契约）：与迁移前 Collections.unmodifiableList 行为等价
    fun itemHeights(): List<Float?> = readOnlyCopy(itemHeightsField)

    fun itemCount(): Int = itemHeightsField.size

    // -------- 布局算法 --------

    override fun viewportWidth(w: Float): VirtualMasonry {
        super.viewportWidth(w)
        recomputeLayouts()
        return this
    }

    /** 列宽：itemWidth>0 用之，否则按视窗宽度在 columns 列平分。 */
    fun columnWidth(): Float {
        if (itemWidthField > 0f) {
            return itemWidthField
        }
        val vw = viewportWidth()
        if (vw <= 0f) {
            return 0f
        }
        return (vw - (columnsField - 1) * gapXField) / columnsField
    }

    /** 「最短列优先」算法：为每项分配列并算出 x/y，缓存结果。 */
    fun recomputeLayouts() {
        val n = itemHeightsField.size
        layoutX = FloatArray(n)
        layoutY = FloatArray(n)
        layoutH = FloatArray(n)
        colWidth = columnWidth()
        val columnBottom = FloatArray(columnsField)
        for (i in 0 until n) {
            val shortest = argMin(columnBottom)
            val x = shortest * (colWidth + gapXField)
            val y = columnBottom[shortest]
            val h = itemHeightsField[i] ?: 0f
            layoutX[i] = x
            layoutY[i] = y
            layoutH[i] = h
            columnBottom[shortest] = y + h + gapYField
        }
        // 末行不需要尾随 gapY。
        val maxBottom = columnBottom.maxOrNull() ?: 0f
        totalHeight = (maxBottom - gapYField).coerceAtLeast(0f)
    }

    private fun argMin(arr: FloatArray): Int {
        var idx = 0
        for (i in 1 until arr.size) {
            if (arr[i] < arr[idx]) {
                idx = i
            }
        }
        return idx
    }

    fun itemX(index: Int): Float = if (inRange(index)) layoutX[index] else 0f

    fun itemY(index: Int): Float = if (inRange(index)) layoutY[index] else 0f

    fun itemHeight(index: Int): Float = if (inRange(index)) layoutH[index] else 0f

    private fun inRange(index: Int): Boolean = index in layoutH.indices

    // -------- 内容尺寸 --------

    override fun requiresContentMeasure(): Boolean = false

    override fun contentHeight(): Float = totalHeight

    override fun contentWidth(): Float = viewportWidth()

    // -------- 窗口范围 --------

    fun firstVisible(): Int {
        val n = layoutH.size
        val top = scrollY() - bufferField
        for (i in 0 until n) {
            if (layoutY[i] + layoutH[i] >= top) {
                return i
            }
        }
        return if (n > 0) n - 1 else 0
    }

    fun lastVisible(): Int {
        val n = layoutH.size
        val bottom = scrollY() + viewportHeight() + bufferField
        var last = -1
        for (i in 0 until n) {
            if (layoutY[i] > bottom) {
                break
            }
            last = i
        }
        return last
    }

    // -------- 子节点管理 --------

    override fun children(): List<UIElement<*>> = windowChildren

    fun setWindowChildren(children: List<UIElement<*>>?) {
        windowChildren = children ?: emptyList()
        // FR-30 B1：windowChildren 经 children() 返回、绕过会标脏的 child()/replaceChildren()，
        // 瀑布流滚动实例化/卸载项时须显式标布局脏，否则整树布局 memo 命中返回过期布局、新窗口项不被定位（内容消失 / 堆顶）。
        markLayoutDirty()
    }

    fun materializeWindow(m: WindowMaterializer) {
        materializer = m
    }

    override fun onScrollStateChanged() {
        super.onScrollStateChanged()
        invalidateWindow()
    }

    private fun invalidateWindow() {
        val m = materializer ?: return
        val count = itemHeightsField.size
        if (count <= 0) {
            if (windowChildren.isNotEmpty()) {
                lastFirst = -1
                lastLast = -1
                lastCount = 0
                m.onWindowChanged(this, 0, -1)
            }
            return
        }
        val first = firstVisible()
        val last = lastVisible()
        if (first == lastFirst && last == lastLast && count == lastCount) {
            return
        }
        lastFirst = first
        lastLast = last
        lastCount = count
        m.onWindowChanged(this, first, last)
    }

    fun interface WindowMaterializer {
        /**
         * 窗口范围变化时调用。
         *
         * @param masonry VirtualMasonry 实例
         * @param first   第一个需实例化的项 index（含）
         * @param last    最后一个需实例化的项 index（含）；空列表时 last < first
         */
        fun onWindowChanged(masonry: VirtualMasonry, first: Int, last: Int)
    }

    companion object {
        @JvmStatic
        fun create(): VirtualMasonry = VirtualMasonry()
    }
}
